import { useReducer, useCallback } from 'react';
import { WIZARD_ACTIONS } from '../constants';
import { VISIBILITY, SHAPE_TYPE } from '../../utils/constants';
import { setThemeAtRank, clearThemeAtRank } from '../../utils/taxonomy';

const initialState = {
  address: '',
  latLng: null,
  shapeType: SHAPE_TYPE.MARKER,
  polygonCoordinates: [],
  lineCoordinates: [],
  year: '',
  placeName: '',
  caption: '',
  storyText: '',
  files: [],
  additionalFields: {},
  visibility: VISIBILITY.PUBLIC,
  // Slice 5 (Phase 3 #9): chip ids the contributor has opted into for this
  // submission. Always carried as numbers; the wizard normalizes to numeric
  // on every dispatch so submitDynDataNU.php's strict array-of-int parse
  // never blows up on stringified ids from <input value> coercions.
  //
  // PR #45: `taxonomyIds` is now derived at submit-time from `themeRanks`
  // (Primary/Secondary/Tertiary) + `propertyTypeId` (single). The legacy
  // TOGGLE_TAXONOMY_ID action still mutates `taxonomyIds` directly for
  // back-compat with any caller that hasn't migrated to the ranked
  // model — useful for tests / future "import suggested chips" flows.
  taxonomyIds: [],
  // PR #45 — v2 ranked themes. Stored as a fixed-shape map so React can
  // diff a stable key set and the UI doesn't have to handle a sparse
  // array. Each value is a theme taxonomy id or null. Ranks are 1-based
  // to match the DB column (`rank` 1..3).
  themeRanks: { 1: null, 2: null, 3: null },
  // Client July 2026 (#7): Property Type and Communities now use the same
  // ranked Primary/Secondary/Tertiary model as Themes — a fixed-shape
  // `{1,2,3}` map of ids (or null) each. (Supersedes the earlier
  // single-select spec D-LOCK-3.)
  propertyTypeRanks: { 1: null, 2: null, 3: null },
  communityRanks: { 1: null, 2: null, 3: null },
  // LEGACY single-select ids, retained so any un-migrated caller/test that
  // still dispatches SET_PROPERTY_TYPE_ID / SET_COMMUNITY_ID keeps working.
  // Not written by the picker anymore; buildTaxonomyPayload still folds them
  // into the id set if present.
  propertyTypeId: null,
  communityId: null,
  formId: null,
  formFields: [],
  contFirstName: '',
  contLastName: '',
  contEmail: '',
};

function wizardReducer(state, action) {
  switch (action.type) {
    case WIZARD_ACTIONS.SET_ADDRESS:
      return { ...state, address: action.payload };
    case WIZARD_ACTIONS.SET_LATLNG:
      return { ...state, latLng: action.payload };
    case WIZARD_ACTIONS.SET_SHAPE_TYPE:
      return { ...state, shapeType: action.payload };
    case WIZARD_ACTIONS.SET_POLYGON_COORDINATES:
      return { ...state, polygonCoordinates: action.payload };
    case WIZARD_ACTIONS.SET_LINE_COORDINATES:
      return { ...state, lineCoordinates: action.payload };
    case WIZARD_ACTIONS.SET_YEAR:
      return { ...state, year: action.payload };
    case WIZARD_ACTIONS.SET_PLACE_NAME:
      return { ...state, placeName: action.payload };
    case WIZARD_ACTIONS.SET_CAPTION:
      return { ...state, caption: action.payload };
    case WIZARD_ACTIONS.SET_STORY_TEXT:
      return { ...state, storyText: action.payload };
    case WIZARD_ACTIONS.SET_FILES:
      return { ...state, files: action.payload };
    case WIZARD_ACTIONS.SET_ADDITIONAL_FIELD:
      return {
        ...state,
        additionalFields: {
          ...state.additionalFields,
          [action.payload.fieldId]: action.payload.value,
        },
      };
    case WIZARD_ACTIONS.SET_VISIBILITY:
      return { ...state, visibility: action.payload };
    case WIZARD_ACTIONS.SET_TAXONOMY_IDS: {
      // Wholesale replace + dedupe — used by RESET-equivalent flows or by
      // future "import suggested chips" actions. Coerces to numbers so the
      // submit pipeline never has to second-guess the type.
      const incoming = Array.isArray(action.payload) ? action.payload : [];
      const dedup = Array.from(
        new Set(incoming.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0)),
      );
      return { ...state, taxonomyIds: dedup };
    }
    case WIZARD_ACTIONS.TOGGLE_TAXONOMY_ID: {
      // Toggle a single chip on/off. The wizard chip picker dispatches this
      // for every TaxonomyChip click in AdditionalInfoStep.
      const id = Number(action.payload);
      if (!Number.isFinite(id) || id <= 0) return state;
      const has = state.taxonomyIds.includes(id);
      const next = has
        ? state.taxonomyIds.filter((x) => x !== id)
        : [...state.taxonomyIds, id];
      return { ...state, taxonomyIds: next };
    }
    case WIZARD_ACTIONS.SET_THEME_AT_RANK: {
      // Assign a theme id to a specific rank slot (1/2/3) via the shared
      // pure helper — same rank semantics the ContributionComp picker
      // uses (dedupe across slots, validation). One source of truth.
      const nextRanks = setThemeAtRank(
        state.themeRanks,
        action.payload?.rank,
        action.payload?.id,
      );
      if (nextRanks === state.themeRanks) return state; // invalid input
      return { ...state, themeRanks: nextRanks };
    }
    case WIZARD_ACTIONS.CLEAR_THEME_AT_RANK: {
      // Clear a rank slot and compact higher ranks down (shared helper).
      const nextRanks = clearThemeAtRank(
        state.themeRanks,
        action.payload?.rank,
      );
      if (nextRanks === state.themeRanks) return state; // invalid input
      return { ...state, themeRanks: nextRanks };
    }
    case WIZARD_ACTIONS.SET_PROPERTY_TYPE_AT_RANK: {
      // Client July 2026 (#7): Property Type is ranked like Themes. Same
      // kind-agnostic helper, applied to its own rank map.
      const nextRanks = setThemeAtRank(
        state.propertyTypeRanks,
        action.payload?.rank,
        action.payload?.id,
      );
      if (nextRanks === state.propertyTypeRanks) return state; // invalid input
      return { ...state, propertyTypeRanks: nextRanks };
    }
    case WIZARD_ACTIONS.CLEAR_PROPERTY_TYPE_AT_RANK: {
      const nextRanks = clearThemeAtRank(
        state.propertyTypeRanks,
        action.payload?.rank,
      );
      if (nextRanks === state.propertyTypeRanks) return state; // invalid input
      return { ...state, propertyTypeRanks: nextRanks };
    }
    case WIZARD_ACTIONS.SET_COMMUNITY_AT_RANK: {
      const nextRanks = setThemeAtRank(
        state.communityRanks,
        action.payload?.rank,
        action.payload?.id,
      );
      if (nextRanks === state.communityRanks) return state; // invalid input
      return { ...state, communityRanks: nextRanks };
    }
    case WIZARD_ACTIONS.CLEAR_COMMUNITY_AT_RANK: {
      const nextRanks = clearThemeAtRank(
        state.communityRanks,
        action.payload?.rank,
      );
      if (nextRanks === state.communityRanks) return state; // invalid input
      return { ...state, communityRanks: nextRanks };
    }
    case WIZARD_ACTIONS.SET_PROPERTY_TYPE_ID: {
      // Single-select Property Type. `null` clears it (clicking the
      // active chip in the UI). Number coercion + validation keeps the
      // submit payload clean.
      const raw = action.payload;
      if (raw == null) return { ...state, propertyTypeId: null };
      const id = Number(raw);
      if (!Number.isFinite(id) || id <= 0) return state;
      return { ...state, propertyTypeId: id };
    }
    case WIZARD_ACTIONS.SET_COMMUNITY_ID: {
      const raw = action.payload;
      if (raw == null) return { ...state, communityId: null };
      const id = Number(raw);
      if (!Number.isFinite(id) || id <= 0) return state;
      return { ...state, communityId: id };
    }
    case WIZARD_ACTIONS.SET_FORM_ID:
      return { ...state, formId: action.payload };
    case WIZARD_ACTIONS.SET_FORM_FIELDS:
      return { ...state, formFields: action.payload };
    case WIZARD_ACTIONS.SET_CONT_FIRST_NAME:
      return { ...state, contFirstName: action.payload };
    case WIZARD_ACTIONS.SET_CONT_LAST_NAME:
      return { ...state, contLastName: action.payload };
    case WIZARD_ACTIONS.SET_CONT_EMAIL:
      return { ...state, contEmail: action.payload };
    case WIZARD_ACTIONS.RESET:
      return { ...initialState, visibility: action.payload?.defaultVisibility || VISIBILITY.PUBLIC };
    default:
      return state;
  }
}

export default function useWizardState(defaultVisibility, { user, isExhibitionMode } = {}) {
  const [state, dispatch] = useReducer(wizardReducer, {
    ...initialState,
    visibility: defaultVisibility || VISIBILITY.PUBLIC,
  });

  const isStepValid = useCallback(
    (stepNumber) => {
      switch (stepNumber) {
        case 1:
          if (state.shapeType === SHAPE_TYPE.POLYGON) {
            return Array.isArray(state.polygonCoordinates) && state.polygonCoordinates.length >= 3;
          }
          if (state.shapeType === SHAPE_TYPE.LINE) {
            return Array.isArray(state.lineCoordinates) && state.lineCoordinates.length >= 2;
          }
          return Boolean(state.latLng);
        case 2:
          return Boolean(state.year && String(state.year).trim());
        case 3:
          return Boolean(state.placeName && state.placeName.trim());
        case 4:
          return true;
        case 5:
          return true;
        case 6: {
          if (user && !isExhibitionMode) return true;
          return Boolean(
            state.contFirstName?.trim() &&
            state.contLastName?.trim() &&
            state.contEmail?.trim()
          );
        }
        default:
          return false;
      }
    },
    [state.latLng, state.shapeType, state.polygonCoordinates, state.lineCoordinates, state.year,
     state.placeName,
     state.contFirstName, state.contLastName, state.contEmail, user, isExhibitionMode],
  );

  return { state, dispatch, isStepValid };
}
